import { FC } from 'react';
import { alpha, Box, Button, CircularProgress, Divider, Stack, TextField, Typography } from '@mui/material';
import { useProcessReportModalContext } from '../ProcessReportModalContext';

const ProcessReportOutputPanel: FC = () => {
  const {
    inputsSummary,
    processButtonLabel,
    canProcess,
    runProcess,
    isPipelineBusy,
    isStartingExtraction,
    isExtractionProcessing,
    isDrafting,
    isExtractionStatusError,
    isExtractionFailed,
    pipelineStatusColor,
    pipelineStatusMessage,
    latestExtractionRun,
    dashboardOpinionUrl,
    draftEmail,
    draftSubject,
    handleSubjectChange,
    draftBody,
    handleBodyChange,
    saveDraftedEmail,
    draftDashboardUrl,
    handleCopyEmail,
  } = useProcessReportModalContext();

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {inputsSummary}
      </Typography>
      <Button
        variant="contained"
        onClick={() => runProcess()}
        disabled={!canProcess}
        id="draft-final-report-email-button">
        {processButtonLabel}
      </Button>
      {(isPipelineBusy || isExtractionStatusError || isExtractionFailed) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: pipelineStatusColor,
          }}>
          {(isExtractionProcessing || isDrafting || isStartingExtraction) && <CircularProgress size={16} />}
          <Typography variant="caption">{pipelineStatusMessage}</Typography>
        </Box>
      )}
      {latestExtractionRun?.run_id && !isPipelineBusy && (
        <Box
          sx={{
            p: 1.5,
            borderRadius: 1,
            border: theme => `1px solid ${theme.palette.divider}`,
            bgcolor: 'action.hover',
          }}
          data-testid="latest-extraction-run-banner">
          <Typography variant="body2" color="text.secondary">
            Last extraction
            {latestExtractionRun.created_at
              ? ` (${new Date(latestExtractionRun.created_at).toLocaleString()})`
              : ''}: {latestExtractionRun.status ?? 'unknown'}
          </Typography>
          {dashboardOpinionUrl ? (
            <Button
              component="a"
              href={dashboardOpinionUrl}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              sx={{ mt: 0.5, p: 0, minWidth: 0, textTransform: 'none' }}
              id="view-dashboard-opinion-link"
              data-testid="view-dashboard-opinion-link">
              View on dashboard
            </Button>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Dashboard link will appear after extraction completes and data is imported.
            </Typography>
          )}
        </Box>
      )}
      <TextField
        label="Subject"
        value={draftSubject}
        onChange={event => handleSubjectChange(event.target.value)}
        onBlur={saveDraftedEmail}
        placeholder="Subject will appear here shortly after you start…"
        fullWidth
        size="small"
        disabled={isDrafting}
        id="final-report-email-subject"
      />
      <Box sx={{ position: 'relative' }}>
        <TextField
          label="Email"
          value={draftBody}
          onChange={event => handleBodyChange(event.target.value)}
          onBlur={saveDraftedEmail}
          placeholder="Email will appear here shortly after you start…"
          multiline
          minRows={12}
          fullWidth
          disabled={isDrafting}
          id="final-report-email-content"
        />
        {isDrafting && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              borderRadius: 1,
              bgcolor: theme => alpha(theme.palette.background.paper, 0.6),
              pointerEvents: 'none',
            }}
            data-testid="final-report-email-loading">
            <CircularProgress size={20} />
            <Typography variant="caption" color="text.secondary">
              Drafting email…
            </Typography>
          </Box>
        )}
      </Box>
      {draftDashboardUrl && (
        <Button
          component="a"
          href={draftDashboardUrl}
          target="_blank"
          rel="noopener noreferrer"
          size="small"
          sx={{ alignSelf: 'flex-start', p: 0, minWidth: 0, textTransform: 'none' }}
          id="draft-dashboard-opinion-link"
          data-testid="draft-dashboard-opinion-link">
          View on dashboard
        </Button>
      )}
      <Divider />
      <Stack direction="row" spacing={1}>
        <Button variant="outlined" onClick={handleCopyEmail} disabled={!draftEmail} id="copy-final-report-email-button">
          Copy Email
        </Button>
      </Stack>
    </Stack>
  );
};

export default ProcessReportOutputPanel;
